import axios from 'axios';

// In development the CRA proxy (setupProxy.js) forwards /api and /auth to the
// Express server, so we use relative URLs.  In production when deployed as a
// single app (backend serves the React build) relative URLs also work.
// For separate frontend/backend Azure Web Apps, set REACT_APP_API_BASE_URL.
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Detect store / admin access mode from the URL (mirrors App.js logic)
const _params = new URLSearchParams(window.location.search);
const _path = window.location.pathname;
const _accessMode = (() => {
  if (_path === '/order-editor' && _params.get('CentralAdmin') === 'true' && _params.get('code') === '2026') return 'admin';
  if (_path === '/order-editor' && (_params.get('hubspotOrderId') || _params.get('orderId') || _params.get('storeId'))) return 'store';
  return null;
})();

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
    ...(_accessMode ? { 'X-Access-Mode': _accessMode } : {}),
  },
  withCredentials: true,
});

// Auth-aware base instance (for /auth/* routes that sit outside /api)
export const authApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Intercept 401 responses — log for debugging but don't auto-redirect
// (AuthContext handles login gating at the UI level)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn('API 401 — session may have expired:', error.config?.url);
    }
    return Promise.reject(error);
  }
);

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const getProductStock = (id, siteId) => api.get(`/products/${id}/stock`, { params: { siteId } });
export const getProductSubstitutions = (id, siteId) => api.get(`/products/${id}/substitutions`, { params: { siteId } });
export const getNextAvailability = (id, siteId) => api.get(`/products/${id}/next-availability`, { params: { siteId } });
export const findSubstitutes = (id, criteria) => api.post(`/products/${id}/find-substitutes`, criteria);
export const getProductCategories = () => api.get('/products/categories');
export const getProductAllergens = () => api.get('/products/allergens');
export const getProductLifecycleStages = () => api.get('/products/lifecycle-stages');
export const getProductFreeFromTags = () => api.get('/products/freefrom-tags');
export const getProductDietaryTags = () => api.get('/products/dietary-tags');

// Packs
export const getPacks = () => api.get('/packs');
export const getPack = (id) => api.get(`/packs/${id}`);
export const getPackStockCheck = (id, siteId, quantity) => api.get(`/packs/${id}/stock-check`, { params: { siteId, quantity } });

// Sites
export const getSites = () => api.get('/sites');
export const getSiteStock = (id) => api.get(`/sites/${id}/stock`);

// Orders
export const getOrders = () => api.get('/orders');
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post('/orders', data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const getSubstitutionReasons = () => api.get('/orders/substitution-reasons');

// Order Lines
export const addOrderLine = (orderId, data) => api.post(`/orders/${orderId}/lines`, data);
export const addPackToOrder = (orderId, packId, quantity) => api.post(`/orders/${orderId}/lines`, { PackId: packId, Quantity: quantity });
export const updateOrderLine = (orderId, lineId, data) => api.put(`/orders/${orderId}/lines/${lineId}`, data);
export const deleteOrderLine = (orderId, lineId) => api.delete(`/orders/${orderId}/lines/${lineId}`);

// Stock Check
export const runStockCheck = (orderId) => api.post(`/orders/${orderId}/stock-check`);

// Substitution
export const substituteLineItem = (orderId, lineId, data) => api.post(`/orders/${orderId}/lines/${lineId}/substitute`, data);

// Submit
export const submitOrder = (orderId) => api.post(`/orders/${orderId}/submit`);

// Audit
export const getOrderAudit = (orderId) => api.get(`/orders/${orderId}/audit`);

// ========================
// Integrations
// ========================

// Combined status & logs
export const getIntegrationStatus = () => api.get('/integrations/status');
export const getIntegrationLogs = (limit = 50) => api.get('/integrations/logs', { params: { limit } });
export const syncAll = () => api.post('/integrations/sync-all');

// Pimberly PIM
export const testPimberlyConnection = () => api.post('/integrations/pimberly/test');
export const syncPimberly = () => api.post('/integrations/pimberly/sync');
export const updatePimberlyConfig = (config) => api.put('/integrations/pimberly/config', config);
export const previewPimberly = () => api.get('/integrations/pimberly/preview');
export const getPimberlyStatus = () => api.get('/integrations/pimberly/status');

// On-Prem SQL Gateway
export const testOnPremConnection = () => api.post('/integrations/onprem/test');
export const syncOnPrem = () => api.post('/integrations/onprem/sync');
export const updateOnPremConfig = (config) => api.put('/integrations/onprem/config', config);
export const getOnPremCustomers = () => api.get('/integrations/onprem/customers');
export const getOnPremPrices = () => api.get('/integrations/onprem/prices');
export const getOnPremStock = () => api.get('/integrations/onprem/stock');
export const getOnPremStockBySku = (sku) => api.get(`/integrations/onprem/stock/${sku}`);
export const runCustomQuery = (query) => api.post('/integrations/onprem/query', { query });

// Price list (synced from on-prem)
export const getPriceList = (params) => api.get('/integrations/price-list', { params });

// Users / Session
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const loginUser = (data) => api.post('/users/login', data);
export const getUserSite = (id) => api.get(`/users/${id}/site`);

// HubSpot
export const hubspotLaunch = (data) => api.post('/hubspot/launch', data);
export const hubspotTestConnection = () => api.post('/hubspot/test');
export const hubspotGetDeal = (dealId) => api.get(`/hubspot/deal/${dealId}`);
export const hubspotSubmitOrder = (orderId) => api.post(`/hubspot/submit-order/${orderId}`);
export const hubspotGetLogs = (limit) => api.get('/hubspot/logs', { params: { limit } });
export const hubspotPreviewLines = (orderId) => api.get(`/hubspot/preview-lines/${orderId}`);
export const hubspotPushLines = (orderId, dealId) => api.post(`/hubspot/push-lines/${orderId}`, { dealId });

export default api;
